import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CategorySelectionScreen from "./CategorySelectionScreen";
import QuestionScreen from "./QuestionScreen";
import {
  isQuestionSelectionLimitReached,
  isQuestionValuePresent,
  isSingleSelectQuestion,
} from "./QuestionRenderer";
import ReviewScreen from "./ReviewScreen";
import UserDetailsScreen from "./UserDetailsScreen";
import VerifyMobileScreen from "./VerifyMobileScreen";
import SelectAccountScreen, { getAccountId } from "./SelectAccountScreen";
import {
  extractLinkedAccounts,
  persistAuthTokenFromVerifyResponse,
  persistLoggedInUser,
} from "../shared/mobileAuthUtils";
import { DUMMY_CATEGORIES } from "./dummyData";
import cityData from "../../utils/citydata";
import { http } from "../../api/httpClient";
import { API_PATHS } from "../../api/apiPaths";
import {
  getMobileAuthRequestConfig,
  shouldSkipSendLoginOtp,
} from "../../api/mobileRequestConfig";
import "./requirements.css";
import {
  getRequirementFunnelStepNumber,
  getRequirementTrafficSource,
  REQUIREMENT_SOURCE,
  trackPostRequirementStart,
  trackPostRequirementStep,
} from "../../utils/analytics";
import { getAdsRequirementPageTitle } from "../../utils/routeAnalytics";
import { useUtm } from "../../context/UtmContext";

const SUPPORTED_QUESTION_TYPES = new Set([
  "select",
  "dropdown",
  "radio",
  "range",
  "multiline",
  "text-input",
]);

const CITY_OPTIONS = cityData
  .map((entry) => {
    const city = entry?.City ? String(entry.City).trim() : "";
    const state = entry?.State ? String(entry.State).trim() : "";
    const label = [city, state].filter(Boolean).join(", ");
    if (!label) return null;
    return {
      label: city,
      value: city,
    };
  })
  .filter(Boolean);

const getUserToken = () => {
  if (typeof window === "undefined") return "";
  const tokenKeys = ["userToken", "token", "authToken", "accessToken"];
  for (const key of tokenKeys) {
    const localValue = window.localStorage.getItem(key);
    if (localValue) return localValue;
    const sessionValue = window.sessionStorage.getItem(key);
    if (sessionValue) return sessionValue;
  }
  return "";
};

const getQuestionType = (question) => {
  const rawType = String(
    question?.type || question?.questionType || question?.inputType || "text-input",
  )
    .toLowerCase()
    .trim();
  if (SUPPORTED_QUESTION_TYPES.has(rawType)) return rawType;
  if (rawType === "multi_select" || rawType === "multiselect") return "select";
  if (rawType === "location_input" || rawType === "location") return "dropdown";
  if (rawType === "textarea" || rawType === "text_area") return "multiline";
  if (rawType === "text" || rawType === "input") return "text-input";
  return "text-input";
};

const normalizeQuestionOption = (option) => {
  if (typeof option === "string" || typeof option === "number") {
    return { label: String(option), value: String(option) };
  }

  return {
    label:
      option?.label || option?.name || option?.text || String(option?.value || ""),
    value:
      option?.value || option?.id || option?._id || option?.label || option?.name || "",
    subLabel: option?.subLabel || option?.description || "",
  };
};

const extractQuestionsFromResponse = (responseData) => {
  const rootList = Array.isArray(responseData)
    ? responseData
    : Array.isArray(responseData?.data)
      ? responseData.data
      : [];

  return rootList.flatMap((item) => {
    if (Array.isArray(item?.questions)) {
      return item.questions.map((question) => ({
        ...question,
        sectionId: item.sectionId,
        sectionName: item.sectionName,
      }));
    }
    return item;
  });
};

const normalizeQuestions = (apiQuestions = [], startIndex = 0, sectionMeta = {}) =>
  apiQuestions
    .map((question, index) => {
      const globalIndex = startIndex + index;
      const validation = question?.validation || {};
      const questionType = getQuestionType(question);
      const rawOptions =
        question?.options || question?.choices || question?.values || [];
      const options = Array.isArray(rawOptions)
        ? rawOptions
            .map(normalizeQuestionOption)
            .filter((option) => option.label && option.value)
        : [];
      const questionApiType = String(
        question?.type || question?.questionType || question?.inputType || questionType,
      )
        .toLowerCase()
        .trim();
      const isLocationInput =
        questionApiType === "location_input" || questionApiType === "location";
      const resolvedOptions =
        isLocationInput && options.length === 0 ? CITY_OPTIONS : options;
      const questionId =
        question?.id ||
        question?.questionId ||
        question?._id ||
        question?.qid ||
        `question_${globalIndex + 1}`;

      return {
        id: String(questionId),
        type: questionType,
        apiType: questionApiType,
        prompt:
          question?.questionText ||
          question?.question ||
          question?.label ||
          question?.title ||
          "Question",
        label:
          question?.questionText ||
          question?.label ||
          question?.title ||
          question?.question ||
          "Question",
        pageTitle:
          question?.pageTitle ||
          sectionMeta.sectionName ||
          question?.sectionName ||
          question?.title ||
          question?.label ||
          "",
        pageDescription: question?.pageDescription || question?.description || "",
        description: question?.description || "",
        placeholder: question?.placeholder || "",
        helperLabel: question?.helperLabel || "",
        sectionId: question?.sectionId || sectionMeta.sectionId || "",
        sectionName: question?.sectionName || sectionMeta.sectionName || "",
        sectionSequence: sectionMeta.sequence,
        required: Boolean(question?.required ?? question?.isRequired ?? true),
        hideFieldLabel: Boolean(question?.hideFieldLabel),
        options: resolvedOptions,
        multiSelect:
          Boolean(question?.multiSelect) ||
          String(
            question?.type || question?.questionType || question?.inputType || "",
          ).toLowerCase() === "multi_select",
        minSelect:
          typeof question?.minSelect === "number"
            ? question.minSelect
            : typeof validation?.minSelect === "number"
              ? validation.minSelect
              : undefined,
        maxSelect:
          typeof question?.maxSelect === "number"
            ? question.maxSelect
            : typeof validation?.maxSelect === "number"
              ? validation.maxSelect
              : undefined,
        minLength:
          typeof question?.minLength === "number"
            ? question.minLength
            : typeof validation?.minLength === "number"
              ? validation.minLength
              : undefined,
        maxLength:
          typeof question?.maxLength === "number"
            ? question.maxLength
            : typeof validation?.maxLength === "number"
              ? validation.maxLength
              : undefined,
        pattern:
          typeof question?.pattern === "string"
            ? question.pattern
            : typeof validation?.pattern === "string"
              ? validation.pattern
              : undefined,
        min: typeof question?.min === "number" ? question.min : undefined,
        max: typeof question?.max === "number" ? question.max : undefined,
        step: typeof question?.step === "number" ? question.step : undefined,
      };
    })
    .filter((question) => question.id && question.label);

const extractQuestionSectionsFromResponse = (responseData) => {
  const rootList = Array.isArray(responseData)
    ? responseData
    : Array.isArray(responseData?.data)
      ? responseData.data
      : [];

  const sectionItems = rootList.filter((item) => Array.isArray(item?.questions));
  if (sectionItems.length === 0) {
    const flatQuestions = normalizeQuestions(extractQuestionsFromResponse(responseData));
    return flatQuestions.length
      ? [
          {
            id: "page_1",
            title: "Page 1",
            sequence: 1,
            questions: flatQuestions,
          },
        ]
      : [];
  }

  let globalIndex = 0;
  return [...sectionItems]
    .sort((a, b) => {
      const aSequence = Number(a?.sequence ?? Number.MAX_SAFE_INTEGER);
      const bSequence = Number(b?.sequence ?? Number.MAX_SAFE_INTEGER);
      if (aSequence !== bSequence) return aSequence - bSequence;
      return String(a?._id || "").localeCompare(String(b?._id || ""));
    })
    .map((section, index) => {
      const questions = normalizeQuestions(section.questions || [], globalIndex, {
        sectionId: section.sectionId || section._id || `page_${index + 1}`,
        sectionName: section.sectionName || `Page ${index + 1}`,
        sequence: section.sequence ?? index + 1,
      });
      globalIndex += questions.length;
      return {
        id: section.sectionId || section._id || `page_${index + 1}`,
        title: section.sectionName || `Page ${index + 1}`,
        sequence: section.sequence ?? index + 1,
        questions,
      };
    })
    .filter((section) => section.questions.length > 0);
};

const getCategoryId = (category) => {
  if (category?.name) {
    const normalized = category.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    if (normalized) return normalized;
  }
  return category?.cid || category?._id || "";
};

const normalizeCategories = (apiCategories = []) =>
  apiCategories
    .map((category) => ({
      id: getCategoryId(category),
      label: category?.name || "Unnamed category",
      icon: category?.icon || "/Category.svg",
      description: category?.description || category?.subtitle || "",
      apiId: category?.cid || category?._id || null,
    }))
    .filter((category) => category.id && category.label)
    // Hide the catch-all "Other/Others" bucket (matches the mobile app).
    .filter((category) => !/^others?$/i.test(String(category.label).trim()));

const normalizeCategoryHint = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/%20/g, " ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const findCategoryByHint = (categoryList, rawHint) => {
  const hint = normalizeCategoryHint(rawHint);
  if (!hint) return null;

  return (
    categoryList.find((category) => normalizeCategoryHint(category.id) === hint) ||
    categoryList.find((category) => normalizeCategoryHint(category.apiId) === hint) ||
    categoryList.find((category) => normalizeCategoryHint(category.label) === hint) ||
    null
  );
};

function RevampRequirementsFlow({ directCategoryRoute = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { categoryParam } = useParams();
  const { utmSource } = useUtm();
  const deepLinkCategoryHint = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return (
      params.get("category") ||
      params.get("cat") ||
      params.get("categoryId") ||
      params.get("cid") ||
      categoryParam ||
      ""
    );
  }, [categoryParam, location.search]);
  const shouldBootIntoDirectQuestions =
    Boolean(directCategoryRoute) && Boolean(deepLinkCategoryHint);
  const requirementSource = getRequirementTrafficSource({ directCategoryRoute });
  const adsCategoryParam =
    requirementSource === REQUIREMENT_SOURCE.ADS ? deepLinkCategoryHint : "";
  const trackedFunnelStepsRef = useRef(new Set());
  const [step, setStep] = useState(() =>
    shouldBootIntoDirectQuestions ? "prefill" : "category",
  );
  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [categoriesError, setCategoriesError] = useState("");
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [questionsError, setQuestionsError] = useState("");
  const [noQuestionsFound, setNoQuestionsFound] = useState(false);
  const [isSubmittingRequirement, setIsSubmittingRequirement] = useState(false);
  const [isRequestingLoginOtp, setIsRequestingLoginOtp] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [questionPages, setQuestionPages] = useState([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [phoneNumber, setPhoneNumber] = useState("");
  const [userFullName, setUserFullName] = useState("");
  const [userCity, setUserCity] = useState("");
  const [otpOrderId, setOtpOrderId] = useState("");
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [hasHandledDeepLinkCategory, setHasHandledDeepLinkCategory] = useState(false);

  const currentQuestionPage = useMemo(
    () => questionPages[questionIndex] || null,
    [questionPages, questionIndex],
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step, questionIndex]);

  useEffect(() => {
    if (!adsCategoryParam) return;
    const pageTitle = getAdsRequirementPageTitle(adsCategoryParam);
    const previousTitle = document.title;
    document.title = pageTitle;
    return () => {
      document.title = previousTitle;
    };
  }, [adsCategoryParam]);

  useEffect(() => {
    trackPostRequirementStart({ source: requirementSource, categoryParam: adsCategoryParam });
  }, [requirementSource, adsCategoryParam]);

  useEffect(() => {
    if (step === "prefill") return;
    const funnelStep = getRequirementFunnelStepNumber(step, requirementSource);
    if (!funnelStep) return;

    const trackKey = `${requirementSource}:funnel_step_${funnelStep}`;
    if (trackedFunnelStepsRef.current.has(trackKey)) return;
    trackedFunnelStepsRef.current.add(trackKey);

    trackPostRequirementStep({
      step: funnelStep,
      source: requirementSource,
      categoryParam: adsCategoryParam,
      internalStep: step,
      buttonName:
        step === "category"
          ? "Select category"
          : step === "questions"
            ? "Answer questions"
            : step === "review"
              ? "Review requirement"
              : "Complete details",
    });
  }, [step, requirementSource, adsCategoryParam]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchCategories = async () => {
      setIsLoadingCategories(true);
      setCategoriesError("");
      try {
        const { data: responseData } = await http.get(API_PATHS.CATEGORIES, {
          signal: controller.signal,
        });
        console.log("Categories API response:", responseData);
        const categoryList = Array.isArray(responseData)
          ? responseData
          : responseData?.data || [];
        const normalizedCategories = normalizeCategories(categoryList);

        if (normalizedCategories.length === 0) {
          throw new Error("Category list is empty");
        }

        setCategories(normalizedCategories);
      } catch (error) {
        if (error?.name === "AbortError" || error?.code === "ERR_CANCELED") return;
        setCategories(DUMMY_CATEGORIES);
        setCategoriesError("Unable to load latest categories. Showing fallback list.");
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchCategories();

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!selectedCategory) return;
    const stillExists = categories.some(
      (category) => category.id === selectedCategory.id,
    );
    if (!stillExists) {
      setSelectedCategory(null);
    }
  }, [categories, selectedCategory]);

  const isQuestionAnswered = (question, customAnswers = answers) => {
    if (!question) return false;
    const value = customAnswers[question.id];
    const hasValue =
      value !== undefined &&
      value !== null &&
      (Array.isArray(value) ? value.length > 0 : String(value).trim() !== "");

    if (!question.required && !hasValue) return true;

    if (question.type === "select") {
      const isMultiSelect =
        Boolean(question.multiSelect) ||
        (typeof question.maxSelect === "number" && question.maxSelect > 1);

      if (isMultiSelect) {
        const selectedValues = Array.isArray(value)
          ? value
          : value
            ? [value]
            : [];
        const minSelect =
          typeof question.minSelect === "number"
            ? question.minSelect
            : question.required
              ? 1
              : 0;
        const maxSelect = question.maxSelect;

        if (selectedValues.length < minSelect) return false;
        if (typeof maxSelect === "number" && selectedValues.length > maxSelect) {
          return false;
        }
        return true;
      }
    }

    if (question.type === "range") {
      if (question.options?.length) {
        return value !== undefined && value !== null && String(value).trim() !== "";
      }
      return typeof value === "number";
    }
    if (!hasValue) return false;

    if (typeof value === "string") {
      if (
        typeof question.minLength === "number" &&
        value.length < question.minLength
      ) {
        return false;
      }
      if (
        typeof question.maxLength === "number" &&
        value.length > question.maxLength
      ) {
        return false;
      }
      if (question.pattern) {
        try {
          const regex = new RegExp(question.pattern);
          if (!regex.test(value)) return false;
        } catch (error) {
          console.warn("Invalid validation pattern for question:", question.id);
        }
      }
    }

    return true;
  };

  const getOptionLabel = (question, optionValue) => {
    if (optionValue === undefined || optionValue === null || optionValue === "") {
      return "";
    }
    const matchedOption = question.options?.find(
      (option) => String(option.value) === String(optionValue),
    );
    return matchedOption?.label || String(optionValue);
  };

  const formatAnswerForPayload = (question, answerValue) => {
    if (Array.isArray(answerValue)) {
      return answerValue
        .map((value) => getOptionLabel(question, value))
        .filter(Boolean);
    }
    if (question.options?.length) {
      return getOptionLabel(question, answerValue);
    }
    return answerValue;
  };

  const isEmptyAnswer = (value) => {
    if (value === undefined || value === null) return true;
    if (Array.isArray(value)) return value.length === 0;
    return String(value).trim() === "";
  };

  const getAnswerFromHints = (hintList) => {
    const normalizedHints = hintList.map((hint) => hint.toLowerCase());
    const matchedQuestion = questions.find((question) => {
      const idText = String(question.id || "").toLowerCase();
      const labelText = String(question.label || "").toLowerCase();
      const promptText = String(question.prompt || "").toLowerCase();
      return normalizedHints.some(
        (hint) =>
          idText.includes(hint) ||
          labelText.includes(hint) ||
          promptText.includes(hint),
      );
    });
    if (!matchedQuestion) return "";
    const value = answers[matchedQuestion.id];
    if (isEmptyAnswer(value)) return "";
    return Array.isArray(value)
      ? value.map((item) => String(item)).join(", ")
      : String(value);
  };

  const getSelectedLocation = () => {
    const locationQuestion = questions.find((question) => {
      const apiType = String(question.apiType || "").toLowerCase();
      if (apiType === "location_input" || apiType === "location") return true;
      const idText = String(question.id || "").toLowerCase();
      const labelText = String(question.label || "").toLowerCase();
      const promptText = String(question.prompt || "").toLowerCase();
      return (
        idText.includes("city") ||
        idText.includes("location") ||
        labelText.includes("city") ||
        labelText.includes("location") ||
        promptText.includes("city") ||
        promptText.includes("location")
      );
    });

    if (locationQuestion) {
      const rawValue = answers[locationQuestion.id];
      if (!isEmptyAnswer(rawValue)) {
        const formattedValue = formatAnswerForPayload(locationQuestion, rawValue);
        if (Array.isArray(formattedValue)) {
          return formattedValue.join(", ");
        }
        return String(formattedValue);
      }
    }

    return getAnswerFromHints(["city", "location", "project_location", "property_type"]);
  };

  const buildRequirementPayload = ({ includeUser = true } = {}) => {
    const fields = questions
      .map((question) => {
        const answerValue = answers[question.id];
        if (isEmptyAnswer(answerValue)) return null;
        const formattedAnswer = formatAnswerForPayload(question, answerValue);
        return {
          id: question.id,
          question: question.prompt || question.label || "Question",
          type: question.apiType || question.type,
          answer: formattedAnswer,
        };
      })
      .filter(Boolean);

    const selectedLocation = getSelectedLocation();
    const city = String(userCity || "").trim() || selectedLocation || "";
    const firstName =
      String(userFullName || "").trim() || getAnswerFromHints(["full_name", "name"]) || "User";
    const email = getAnswerFromHints(["email"]);

    const payload = {
      cid: String(selectedCategory?.apiId || selectedCategory?.id || ""),
      fields,
      source: "WEB",
      city,
      maxReactions: 10,
      isNew: true,
    };

    if (utmSource) {
      payload.utmSource = utmSource;
    }

    if (includeUser) {
      payload.user = {
        phoneNumber,
        accountType: "personal",
        location: city,
        firstName,
        email,
      };
    }

    return payload;
  };

  const buildSendLoginOtpPayload = () => ({
    phoneNumber: `91${phoneNumber}`,
  });

  const buildVerifyLoginOtpPayload = (otpCode, orderId) => ({
    phoneNumber: `91${phoneNumber}`,
    otp: otpCode,
    orderId,
    request_id: orderId,
  });

  const requestLoginOtp = async () => {
    if (!phoneNumber || !/^[0-9]{10}$/.test(phoneNumber)) {
      throw new Error("Enter a valid 10-digit mobile number.");
    }
    setIsRequestingLoginOtp(true);
    try {
      const { data } = await http.post(
        API_PATHS.SEND_LOGIN_OTP,
        buildSendLoginOtpPayload(),
        getMobileAuthRequestConfig(),
      );
      console.log("Send login OTP response:", data);
      const resolvedOrderId =
        data?.request_id ||
        data?.orderId ||
        data?.data?.request_id ||
        data?.data?.orderId ||
        data?.result?.request_id ||
        data?.result?.orderId ||
        "";
      console.log("Resolved order ID:", resolvedOrderId);
      if (!resolvedOrderId) {
        throw new Error("OTP orderId missing from send OTP response.");
      }
      setOtpOrderId(String(resolvedOrderId));
    } finally {
      setIsRequestingLoginOtp(false);
    }
  };

  const resendLoginOtp = async () => {
    if (!phoneNumber || !/^[0-9]{10}$/.test(phoneNumber)) {
      throw new Error("Enter a valid 10-digit mobile number.");
    }
    setIsRequestingLoginOtp(true);
    try {
      const { data } = await http.post(
        API_PATHS.RESEND_LOGIN_OTP,
        buildSendLoginOtpPayload(),
        getMobileAuthRequestConfig(),
      );
      const resolvedOrderId =
        data?.request_id ||
        data?.orderId ||
        data?.data?.request_id ||
        data?.data?.orderId ||
        data?.result?.request_id ||
        data?.result?.orderId ||
        otpOrderId ||
        "";
      if (resolvedOrderId) {
        setOtpOrderId(String(resolvedOrderId));
      }
    } finally {
      setIsRequestingLoginOtp(false);
    }
  };

  const handleCategoryContinue = async (categoryOverride) => {
    const targetCategory = categoryOverride || selectedCategory;
    if (!targetCategory || isLoadingCategories || isLoadingQuestions) return;
    if (categoryOverride && categoryOverride.id !== selectedCategory?.id) {
      setSelectedCategory(categoryOverride);
    }
    setIsLoadingQuestions(true);
    setQuestionsError("");
    setNoQuestionsFound(false);

    try {
      const token = getUserToken();
      const headers = token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {};
      const { data: responseData } = await http.get(
        API_PATHS.questionsForCategory(
          targetCategory.apiId || targetCategory.id,
        ),
        { headers },
      );
      console.log("Questions API response:", responseData);
      const normalizedPages = extractQuestionSectionsFromResponse(responseData);
      const normalizedQuestions = normalizedPages.flatMap((page) => page.questions);
      if (normalizedQuestions.length === 0) {
        setQuestionPages([]);
        setQuestions([]);
        setAnswers({});
        setQuestionIndex(0);
        setNoQuestionsFound(true);
        setQuestionsError("No question found for this category.");
        return;
      }

      setQuestionPages(normalizedPages);
      setQuestions(normalizedQuestions);
      setAnswers({});
      setQuestionIndex(0);
      setStep("questions");
    } catch (error) {
      const statusCode = error?.response?.status;
      setQuestionPages([]);
      setQuestions([]);
      setAnswers({});
      setQuestionIndex(0);
      setNoQuestionsFound(statusCode === 404);
      setQuestionsError(
        statusCode === 404
          ? "No question found for this category."
          : "Unable to load questions right now. Please try again.",
      );
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  useEffect(() => {
    if (hasHandledDeepLinkCategory) return;
    if (isLoadingCategories || isLoadingQuestions) return;
    if (step !== "category" && step !== "prefill") return;
    if (!deepLinkCategoryHint) {
      if (step === "prefill") setStep("category");
      setHasHandledDeepLinkCategory(true);
      return;
    }

    const matchedCategory = findCategoryByHint(categories, deepLinkCategoryHint);
    if (!matchedCategory) {
      if (step === "prefill") setStep("category");
      setHasHandledDeepLinkCategory(true);
      return;
    }

    setSelectedCategory(matchedCategory);
    setHasHandledDeepLinkCategory(true);
  }, [
    categories,
    deepLinkCategoryHint,
    hasHandledDeepLinkCategory,
    isLoadingCategories,
    isLoadingQuestions,
    step,
  ]);

  useEffect(() => {
    if (!hasHandledDeepLinkCategory) return;
    if (!deepLinkCategoryHint) return;
    if (step !== "category" && step !== "prefill") return;
    if (!selectedCategory) return;
    if (isLoadingQuestions) return;

    handleCategoryContinue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkCategoryHint, hasHandledDeepLinkCategory, selectedCategory, step]);

  const autoContinueTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (autoContinueTimerRef.current) {
        clearTimeout(autoContinueTimerRef.current);
      }
    };
  }, [questionIndex, step]);

  const handleBack = () => {
    if (autoContinueTimerRef.current) {
      clearTimeout(autoContinueTimerRef.current);
    }
    if (step === "accounts") {
      setStep("verify");
    } else if (step === "questions") {
      if (questionIndex > 0) {
        setQuestionIndex((prev) => prev - 1);
      } else {
        setStep("category");
      }
    } else if (step === "review") {
      setStep("questions");
      setQuestionIndex(Math.max(questionPages.length - 1, 0));
    } else if (step === "userDetails") {
      setStep(linkedAccounts.length > 0 ? "accounts" : "verify");
    } else if (step === "verify") {
      setStep("review");
    }
  };

  const handleContinue = (answersOverride) => {
    if (autoContinueTimerRef.current) {
      clearTimeout(autoContinueTimerRef.current);
    }
    const currentAnswers = answersOverride || answers;
    const pageQuestions = currentQuestionPage?.questions || [];
    const isPageAnswered = pageQuestions.every((item) =>
      isQuestionAnswered(item, currentAnswers),
    );
    if (!pageQuestions.length || !isPageAnswered) return;
    if (questionIndex >= questionPages.length - 1) {
      setStep("review");
      return;
    }
    setQuestionIndex((prev) => prev + 1);
  };

  const handleAnswerChange = (questionId, value) => {
    if (autoContinueTimerRef.current) {
      clearTimeout(autoContinueTimerRef.current);
    }

    const nextAnswers = { ...answers, [questionId]: value };
    setAnswers(nextAnswers);

    const pageQuestions = currentQuestionPage?.questions || [];
    const changedQuestion = pageQuestions.find((q) => q.id === questionId);

    // Auto-advance logic:
    // Only auto-navigate if:
    // 1) The question just answered reached its selection limit:
    //    - Single-select: user selected 1 option.
    //    - Multi-select with maxSelect: user selected all maxSelect options (e.g. all 5 capsulated buttons).
    // 2) ALL questions on this page have been answered by the user (every question has a value present & is valid)
    if (changedQuestion && isQuestionSelectionLimitReached(changedQuestion, value)) {
      const areAllQuestionsAnswered =
        pageQuestions.length > 0 &&
        pageQuestions.every(
          (item) =>
            isQuestionValuePresent(item, nextAnswers) &&
            isQuestionAnswered(item, nextAnswers),
        );

      if (areAllQuestionsAnswered) {
        autoContinueTimerRef.current = setTimeout(() => {
          handleContinue(nextAnswers);
        }, 280);
      }
    }
  };

  const handleSubmit = () => {
    setStep("verify");
  };

  const handlePhoneNumberChange = (nextPhone) => {
    setPhoneNumber(nextPhone);
    if (otpOrderId) {
      setOtpOrderId("");
    }
    setLinkedAccounts([]);
  };

  const submitRequirement = async ({ token = "" } = {}) => {
    if (isSubmittingRequirement) return;

    setIsSubmittingRequirement(true);
    try {
      const requirementPayload = buildRequirementPayload({ includeUser: !token });

      console.log("Requirement submit payload:", requirementPayload);
      const endpoint = token
        ? API_PATHS.SUBMIT_REQUIREMENT
        : API_PATHS.SUBMIT_REQUIREMENT_WITHOUT_TOKEN;
      const requestConfig = token
        ? {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        : undefined;
      const submitResponse = await http.post(endpoint, requirementPayload, requestConfig);
      const responseData = submitResponse?.data;
      console.log("Requirement submit response:", responseData);
      if (responseData && responseData.success === false) {
        throw new Error(responseData?.message || "Failed to submit requirement");
      }
      navigate("/requirement-success", {
        replace: true,
        state: {
          category: selectedCategory?.label,
          requirementSource,
          categoryParam: adsCategoryParam || selectedCategory?.apiId || selectedCategory?.id || "",
        },
      });
    } catch (error) {
      console.error("Requirement submit failed:", error);
      setStep("userDetails");
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Requirement submission failed";
      alert(message);
      throw error;
    } finally {
      setIsSubmittingRequirement(false);
    }
  };

  const handleVerify = async (otp) => {
    const otpCode = otp != null ? String(otp).replace(/\D/g, "") : "";
    if (!phoneNumber || otpCode.length !== 6) {
      alert("Enter the 6-digit OTP sent to your number.");
      throw new Error("Invalid OTP");
    }
    if (!otpOrderId) {
      alert("OTP session expired. Please request OTP again.");
      throw new Error("Missing orderId");
    }
    if (isSubmittingRequirement) return;

    setIsSubmittingRequirement(true);
    try {
      console.log("OTP Code:", otpCode);
      console.log("OTP Order ID:", otpOrderId);
      const verifyPayload = buildVerifyLoginOtpPayload(otpCode, otpOrderId);
      console.log("Verify login OTP payload:", verifyPayload);
      const verifyResponse = await http.post(
        API_PATHS.VERIFY_LOGIN_OTP,
        verifyPayload,
        getMobileAuthRequestConfig(),
      );
      persistAuthTokenFromVerifyResponse(verifyResponse.data);
      const accounts = extractLinkedAccounts(verifyResponse.data);
      if (accounts.length > 0) {
        setLinkedAccounts(accounts);
        setStep("accounts");
        return;
      }
      setStep("userDetails");
    } catch (error) {
      console.error("OTP verify failed:", error);
      setStep("verify");
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "OTP verification failed";
      alert(message);
      throw error;
    } finally {
      setIsSubmittingRequirement(false);
    }
  };

  const handleSelectAccount = async (account) => {
    const accountId = getAccountId(account);
    if (!accountId || isSubmittingRequirement) return;

    setIsSubmittingRequirement(true);
    try {
      const { data } = await http.post(
        API_PATHS.GENERATE_AUTH_TOKEN,
        { userDetails: accountId },
        getMobileAuthRequestConfig(),
      );
      if (data?.status && data.status !== 200) {
        throw new Error(data?.message || "Could not continue with this profile.");
      }
      const user = data?.user || data?.data?.user || account;
      const token =
        data?.authToken ||
        data?.token ||
        data?.data?.authToken ||
        data?.data?.token ||
        "";
      if (!token) {
        throw new Error("Login token missing. Please try again.");
      }
      persistLoggedInUser(user, token);
      setIsSubmittingRequirement(false);
      await submitRequirement({ token });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Could not post this requirement.";
      alert(message);
    } finally {
      setIsSubmittingRequirement(false);
    }
  };

  return (
    <div className="requirements-page">
      <div className="requirements-shell">
        {step === "prefill" && (
          <div className="requirements-loading-state">
            Preparing your questions...
          </div>
        )}

        {step === "category" && (
          <CategorySelectionScreen
            categories={categories}
            isLoadingCategories={isLoadingCategories}
            isLoadingQuestions={isLoadingQuestions}
            categoriesError={categoriesError}
            questionsError={questionsError}
            noQuestionsFound={noQuestionsFound}
            selectedCategoryId={selectedCategory?.id}
            onSelect={(category) => {
              setSelectedCategory(category);
              if (noQuestionsFound) setNoQuestionsFound(false);
              if (questionsError) setQuestionsError("");
            }}
            onContinue={handleCategoryContinue}
          />
        )}

        {step === "questions" && currentQuestionPage && (
          <QuestionScreen
            questions={currentQuestionPage.questions}
            questionIndex={questionIndex}
            totalQuestions={questionPages.length}
            pageTitle={currentQuestionPage.title}
            answers={answers}
            showBack={!(directCategoryRoute && questionIndex === 0)}
            onAnswerChange={handleAnswerChange}
            onBack={handleBack}
            onContinue={() => handleContinue()}
            canContinue={currentQuestionPage.questions.every((item) =>
              isQuestionAnswered(item),
            )}
          />
        )}

        {step === "review" && (
          <ReviewScreen
            selectedCategory={selectedCategory}
            questions={questions}
            answers={answers}
            onBack={handleBack}
            onSubmit={handleSubmit}
          />
        )}

        {step === "verify" && (
          <VerifyMobileScreen
            phoneNumber={phoneNumber}
            onPhoneChange={handlePhoneNumberChange}
            onBack={handleBack}
            onVerify={handleVerify}
            isSubmitting={isSubmittingRequirement}
            onRequestOtp={shouldSkipSendLoginOtp() ? undefined : requestLoginOtp}
            onResendOtp={shouldSkipSendLoginOtp() ? undefined : resendLoginOtp}
            isRequestingOtp={isRequestingLoginOtp}
          />
        )}

        {step === "accounts" && (
          <SelectAccountScreen
            accounts={linkedAccounts}
            phoneNumber={phoneNumber}
            onSelectAccount={handleSelectAccount}
            onCreateNewAccount={() => setStep("userDetails")}
            onBack={handleBack}
            isLoading={isSubmittingRequirement}
          />
        )}

        {step === "userDetails" && (
          <UserDetailsScreen
            fullName={userFullName}
            city={userCity}
            onNameChange={setUserFullName}
            onCityChange={setUserCity}
            cityOptions={CITY_OPTIONS}
            onBack={handleBack}
            onContinue={() => {
              submitRequirement();
            }}
            isSubmitting={isSubmittingRequirement}
            continueLabel="Post the requirement"
          />
        )}

      </div>
    </div>
  );
}

export default RevampRequirementsFlow;
